/**
 * Level Up Popup for Poker Reborn
 *
 * Celebration overlay shown when a player reaches a new level.
 * Displays the level transition, chip reward, and any milestone
 * unlocks (frame, title, badge).
 *
 * Follows the BonusPopup pattern:
 * - Extends Container directly (no base Popup class)
 * - Creates overlay, background panel, content
 * - Auto-destroys on dismiss via onDismiss callback
 *
 * @see docs/features/progress/XP_LEVELS.md - XP/Level feature design
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE } from '@/components/common/Button';
import type { LevelUpData } from '@/services/XpService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  popup: {
    width: 380,
    height: 360,
    widthMobile: 320,
    heightMobile: 320,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
    padding: 24,
    paddingMobile: 18,
  },
  header: {
    fill: 0xffd700,
    fontSize: 28,
    fontSizeMobile: 24,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  levelTransition: {
    fill: 0xffffff,
    fontSize: 20,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  reward: {
    fill: 0x4CAF50,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  milestone: {
    fill: 0xffd54f,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  nextLevel: {
    fill: 0x888888,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// LEVEL UP POPUP
// =============================================================================

export class LevelUpPopup extends Container {
  private overlay: Graphics;
  private panel: Container;
  private popupWidth: number;
  private popupHeight: number;
  private screenWidth: number;
  private screenHeight: number;

  /** Called when popup is dismissed */
  public onDismiss?: () => void;

  constructor(
    screenWidth: number,
    screenHeight: number,
    data: LevelUpData,
  ) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    const isMobile = screenWidth < STYLES.breakpoints.mobile;
    this.popupWidth = isMobile ? STYLES.popup.widthMobile : STYLES.popup.width;
    this.popupHeight = isMobile ? STYLES.popup.heightMobile : STYLES.popup.height;
    const padding = isMobile ? STYLES.popup.paddingMobile : STYLES.popup.padding;

    // Full-screen overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointertap', () => { this.dismiss(); });
    this.addChild(this.overlay);

    // Centered panel
    this.panel = new Container();
    this.panel.x = (screenWidth - this.popupWidth) / 2;
    this.panel.y = (screenHeight - this.popupHeight) / 2;
    this.addChild(this.panel);

    // Panel background
    const panelBg = new Graphics();
    panelBg.roundRect(0, 0, this.popupWidth, this.popupHeight, STYLES.popup.cornerRadius);
    panelBg.fill(STYLES.popup.fill);
    panelBg.setStrokeStyle({ width: 2, color: STYLES.popup.stroke });
    panelBg.stroke();
    panelBg.eventMode = 'static';
    panelBg.on('pointerdown', (e) => { e.stopPropagation(); });
    this.panel.addChild(panelBg);

    // Content layout
    let currentY = padding;
    const centerX = this.popupWidth / 2;

    // "LEVEL UP!" header
    const headerText = new Text({
      text: 'LEVEL UP!',
      style: {
        ...STYLES.header,
        fontSize: isMobile ? STYLES.header.fontSizeMobile : STYLES.header.fontSize,
      },
    });
    headerText.anchor.set(0.5, 0);
    headerText.x = centerX;
    headerText.y = currentY;
    this.panel.addChild(headerText);
    currentY += headerText.height + 16;

    // Level transition: "Level 14 -> 15"
    const transitionText = new Text({
      text: `Level ${String(data.oldLevel)}  \u2192  ${String(data.newLevel)}`,
      style: STYLES.levelTransition,
    });
    transitionText.anchor.set(0.5, 0);
    transitionText.x = centerX;
    transitionText.y = currentY;
    this.panel.addChild(transitionText);
    currentY += transitionText.height + 20;

    // Chip reward
    if (data.reward.chips > 0) {
      const rewardText = new Text({
        text: `+${data.reward.chips.toLocaleString()} chips`,
        style: STYLES.reward,
      });
      rewardText.anchor.set(0.5, 0);
      rewardText.x = centerX;
      rewardText.y = currentY;
      this.panel.addChild(rewardText);
      currentY += rewardText.height + 12;
    }

    // Milestone unlocks (frame, title, badge)
    const milestoneLines: string[] = [];
    if (data.reward.frame) {
      milestoneLines.push(`New frame: ${data.reward.frame}`);
    }
    if (data.reward.title) {
      milestoneLines.push(`New title: ${data.reward.title}`);
    }
    if (data.reward.badge) {
      milestoneLines.push('Badge unlocked!');
    }

    if (milestoneLines.length > 0) {
      const milestoneText = new Text({
        text: milestoneLines.join('\n'),
        style: {
          ...STYLES.milestone,
          align: 'center',
        },
      });
      milestoneText.anchor.set(0.5, 0);
      milestoneText.x = centerX;
      milestoneText.y = currentY;
      this.panel.addChild(milestoneText);
      currentY += milestoneText.height + 12;
    }

    // Next level info
    if (data.xpNeededNext > 0) {
      const nextText = new Text({
        text: `Next: ${data.xpNeededNext.toLocaleString()} XP to Lv.${String(data.newLevel + 1)}`,
        style: STYLES.nextLevel,
      });
      nextText.anchor.set(0.5, 0);
      nextText.x = centerX;
      nextText.y = currentY;
      this.panel.addChild(nextText);
      currentY += nextText.height + 20;
    }

    // OK button
    const buttonWidth = Math.min(this.popupWidth - padding * 2, 200);
    const okButton = new Button({
      label: 'OK',
      width: buttonWidth,
      height: 44,
      style: {
        ...GOLD_BUTTON_STYLE,
        fontSize: 16,
      },
    });
    okButton.x = (this.popupWidth - buttonWidth) / 2;
    okButton.y = this.popupHeight - padding - 44;
    okButton.onClick = () => { this.dismiss(); };
    this.panel.addChild(okButton);

    // Entrance animation
    this.animateEntrance();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    this.panel.x = (width - this.popupWidth) / 2;
    this.panel.y = (height - this.popupHeight) / 2;
  }

  // ===========================================================================
  // PRIVATE
  // ===========================================================================

  private dismiss(): void {
    this.onDismiss?.();
    this.destroy({ children: true });
  }

  private animateEntrance(): void {
    this.panel.alpha = 0;
    this.panel.scale.set(0.8);

    let animationFrame = 0;
    const totalFrames = 20;
    const animate = (): void => {
      if (this.destroyed) return;
      animationFrame++;
      const progress = Math.min(animationFrame / totalFrames, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.panel.alpha = eased;
      this.panel.scale.set(0.8 + 0.2 * eased);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }
}
